import {MouseEventHandler, useState} from "react";
import {useForm} from "react-hook-form";
import {motion, AnimatePresence} from "framer-motion";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import axios from "axios";

const schema = yup.object().shape({
	companyName: yup.string().required("Company name is required"),
	businessSector: yup.string().required("Business sector is required"),
	businessActivity: yup.string().min(20,"Please provide at least 20 characters"),
	numberOfEmployees: yup.number().required("Number of employees is required").min(1, "Number of employees must be at least 1").max(10000,"Number of employees must be less than 10,000"),
	fullName: yup.string().required("Full name is required").min(2,"Name must be at least 2 characters"),
	position: yup.string().required("Position is required"),
	email: yup.string().email("Invalid email format").required("Email is required"),
	phone: yup.string().required("Phone number is required"),
	currentPlatform: yup.string(),
	additionalNotes: yup.string(),
});

export default () => {
	const [step,setStep] = useState(1);
	const [isSubmitting,setIsSubmitting] = useState(false);
	const {register,handleSubmit,formState:{errors},trigger} = useForm({
		resolver: yupResolver(schema),
		mode:"onBlur",
        shouldUnregister:false,

        defaultValues: {
            companyName: '',
            businessSector: '',
            businessActivity: '',
            numberOfEmployees: 0,
            fullName: '',
            position: '',
            email: '',
            phone: '',
            currentPlatform: '',
			additionalNotes: '',
        }
	});

	// Returns sx override to mimic Tailwind input styles.
	const getInputSx = (hasError: boolean) => ({
		"& .MuiOutlinedInput-root": {
			borderRadius:"0.5rem",
			"& fieldset": {
				borderColor: hasError ? "#F87171" : "#D1D5DB"
			},
			"&:hover fieldset": {
				borderColor: hasError ? "#F87171" : "#A78BFA"
			},
			"&.Mui-focused fieldset": {
				borderColor:"#A78BFA",
				boxShadow:"0 0 0 2px rgba(167, 139, 250, 0.5)"
			}
		}
	});

	const onSubmit = async(data: any) => {
		setIsSubmitting(true);
		try{
            const config = { headers: { "Content-Type": "application/json", }, };

            // Send the claim data to the API
            const submitResponse = await axios.post("https://bm8gsbltcl.execute-api.eu-west-2.amazonaws.com/dev/func_numasuite_lead_form", { data: data }, config);
            if(submitResponse.status !== 200) {
                console.error("Failed to submit form: " + submitResponse.status + " - " + (submitResponse.data ? submitResponse.data.message : ""));
                alert('Failed to submit your details, please try again later.');
            } else {
                console.log("Successfully submitted claim record: " + submitResponse.status);
                setStep(4);
            }
		} catch(error) {
			console.error("Submission error: ",error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const nextStep : MouseEventHandler<HTMLButtonElement> = async (event) => {
		event.preventDefault();
		const fields = getFieldsByStep(step);
		const isStepValid = await trigger(fields as any);
		if(isStepValid) setStep(step+1);
	};

	const getFieldsByStep = (step: number) => {
		switch(step){
			case 1: return ["companyName","businessSector","businessActivity","numberOfEmployees"];
			case 2: return ["fullName","position","email","phone"];
			case 3: return ["currentPlatform","additionalNotes"];
			default: return [];
		}
	};

	const FormStep1 = () => (
		<motion.div key="form-step1" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
			<Box sx={{display:"flex",flexDirection:"column",gap:2}}>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Company Name *</Typography>
					<TextField {...register("companyName")} tabIndex={1} type="text" fullWidth variant="outlined"
						error={!!errors.companyName} helperText={errors.companyName?.message} sx={getInputSx(!!errors.companyName)}
					/>
				</Box>
				<Box sx={{display:"flex",flexDirection:"column",gap:2}}>
					<Box>
						<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Business Sector *</Typography>
						<TextField {...register("businessSector")} tabIndex={2} fullWidth variant="outlined"
							error={!!errors.businessSector} helperText={errors.businessSector?.message} sx={getInputSx(!!errors.businessSector)}
						/>
					</Box>
					<Box>
						<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>What Does Your Business Do? *</Typography>
						<TextField {...register("businessActivity")} tabIndex={3} multiline rows={4} fullWidth variant="outlined"
							error={!!errors.businessActivity} helperText={errors.businessActivity?.message} sx={getInputSx(!!errors.businessActivity)}
						/>
					</Box>
				</Box>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Number of Employees *</Typography>
					<TextField {...register("numberOfEmployees")} tabIndex={4} type="number" fullWidth variant="outlined"
						error={!!errors.numberOfEmployees} helperText={errors.numberOfEmployees?.message} sx={getInputSx(!!errors.numberOfEmployees)}
					/>
				</Box>
			</Box>
		</motion.div>
	);

	const FormStep2 = () => (
		<motion.div key="form-step2" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
			<Box sx={{display:"flex",flexDirection:"column",gap:2}}>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Full Name *</Typography>
					<TextField {...register("fullName")} tabIndex={1} type="text" fullWidth variant="outlined"
						error={!!errors.fullName} helperText={errors.fullName?.message} sx={getInputSx(!!errors.fullName)}
					/>
				</Box>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Position</Typography>
					<TextField {...register("position")} tabIndex={2} type="text" fullWidth variant="outlined" 
						error={!!errors.position} helperText={errors.position?.message} sx={getInputSx(!!errors.position)} 
					/>
				</Box>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Email *</Typography>
					<TextField {...register("email")} tabIndex={3} type="email" fullWidth variant="outlined"
						error={!!errors.email} helperText={errors.email?.message} sx={getInputSx(!!errors.email)}
					/>
				</Box>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>Phone *</Typography>
					<TextField {...register("phone")} tabIndex={4} type="tel" fullWidth variant="outlined"
						error={!!errors.phone} helperText={errors.phone?.message} sx={getInputSx(!!errors.phone)}
					/>
				</Box>
			</Box>
		</motion.div>
	);

	const FormStep3 = () => (
		<motion.div key="form-step3" initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-20}}>
			<Box sx={{display:"flex",flexDirection:"column",gap:2}}>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>
						What Platforms Are You Currently Using, If Any?
					</Typography>
					<TextField {...register("currentPlatform")} multiline minRows={3} tabIndex={1} type="text" fullWidth variant="outlined"
						error={!!errors.currentPlatform} helperText={errors.currentPlatform?.message} sx={getInputSx(!!errors.currentPlatform)}
					/>
				</Box>
				<Box>
					<Typography variant="body2" sx={{fontWeight:500,color:"#4A5568",mb:1}}>
						Is There Anything Else You Would Like Us To Know?
					</Typography>
					<TextField {...register("additionalNotes")} multiline minRows={3} tabIndex={2} type="text" fullWidth variant="outlined"
						error={!!errors.additionalNotes} helperText={errors.additionalNotes?.message} sx={getInputSx(!!errors.additionalNotes)}
					/>
				</Box>
			</Box>
		</motion.div>
	);

	const SuccessStep = () => (
		<motion.div key="form-step4" initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}}>
			<Box sx={{textAlign:"center",display:"flex",flexDirection:"column",gap:2}}>
				{/* <FiCheckCircle style={{fontSize:"3rem",color:"#48BB78",margin:"auto"}} /> */}
				<Typography variant="h4" sx={{fontWeight:"bold",color:"#2D3748"}}>
					Thank You!
				</Typography>
				<Typography sx={{color:"#718096"}}>
					We&apos;ll be in touch with you shortly.
				</Typography>
			</Box>
		</motion.div>
	);

	return (
		<Container
			maxWidth="lg"
			sx={{
				minHeight:"100vh",
				py:4,
				px:{xs:4,sm:6,lg:8}
			}}
		>
			<Paper
				sx={{
					maxWidth:640,
					margin:"auto",
					borderRadius:"0.75rem",
					p:4,
					backgroundColor:"#ffffff",
					boxShadow:3
				}}
			>
				<Box sx={{mb:1}}>
					<Box sx={{display:"flex",justifyContent:"center",gap:1,mb:4}}>
						{[1,2,3].map(i => (
							<Box
								key={i}
								sx={{
									width:12,
									height:12,
									borderRadius:"50%",
									backgroundColor: step>=i ? "#6B46C1" : "#E5E7EB"
								}}
							/>
						))}
					</Box>
				</Box>
				<form onSubmit={handleSubmit(onSubmit)} noValidate>
					<AnimatePresence mode="wait">
						{step===1 && <FormStep1 />}
						{step===2 && <FormStep2 />}
						{step===3 && <FormStep3 />}
						{step===4 && <SuccessStep />}
					</AnimatePresence>
					{step < 4 && (
						<Box sx={{display:"flex",justifyContent:"space-between",pt:4}}>
							{step > 1 && (
								<Button
									type="button"
									onClick={()=>setStep(step-1)}
									// startIcon={<FiArrowLeft />}
									variant="text"
									sx={{color:"#6B46C1",textTransform:"none"}}
								>
									Back
								</Button>
							)}
							{step < 3 ? (
								<Button
									type="button"
									onClick={nextStep}
									// endIcon={<FiArrowRight />}
									variant="contained"
									sx={{
										backgroundColor:"#6B46C1",
										textTransform:"none",
										ml:"auto",
										":hover": {backgroundColor:"#553C9A"}
									}}
								>
									Next
								</Button>
							) : (
								<Button
									type="submit"
									disabled={isSubmitting}
									variant="contained"
									sx={{
										backgroundColor:"#6B46C1",
										textTransform:"none",
										ml:"auto",
										":hover": {backgroundColor:"#553C9A"},
										disabled:{opacity:0.5}
									}}
								>
									{isSubmitting ? "Submitting..." : "Get Started"}
								</Button>
							)}
						</Box>
					)}
				</form>
			</Paper>
		</Container>
	);
};